import { defineStore } from 'pinia'
import { RemovableRef, useLocalStorage } from '@vueuse/core'
import { getPersonalAccountValidationSchema, handleDirtyState } from './useAccountFormPersonal'
import { NavigationItemParent } from '../../typings'
import { i18n } from '../../localization'
import {
    BadgeCheckIcon,
    CashIcon,
    CollectionIcon,
    CreditCardIcon,
    DocumentTextIcon,
    FolderIcon,
    GiftIcon,
    HomeIcon,
    LockClosedIcon,
    PencilAltIcon,
    PhoneIcon,
    ReceiptTaxIcon,
    UserGroupIcon,
    UsersIcon,
    TicketIcon,
} from '@heroicons/vue/outline'

import {
    BadgeCheckIcon as BadgeCheckIconFilled,
    CashIcon as CashIconFilled,
    DocumentTextIcon as DocumentTextIconFilled,
    FolderIcon as FolderIconFilled,
    GiftIcon as GiftIconFilled,
    HomeIcon as HomeIconFilled,
    LockClosedIcon as LockClosedIconFilled,
    PencilAltIcon as PencilAltIconFilled,
    PhoneIcon as PhoneIconFilled,
    ReceiptTaxIcon as ReceiptTaxIconFilled,
    UsersIcon as UsersIconFilled,
    UserGroupIcon as UserGroupIconFilled,
} from '@heroicons/vue/solid'
import { useForm } from 'vee-validate'
import { usePersonalAccountDataState } from './useAccountDataPersonal'
import { getOrdersLink, getWalletLink, getSecondaryMarketLink } from '../useLinks'

export const navigationItemsPersonal: NavigationItemParent[] = [
    {
        name: i18n.global.t('account_settings.dashboard'),
        icon: HomeIcon,
        iconActive: HomeIconFilled,
        component: 'Dashboard',
    },
    {
        name: i18n.global.t('account_settings.overview'),
        children: [
            {
                name: i18n.global.t('account_settings.personal_data'),
                icon: UsersIcon,
                iconActive: UsersIconFilled,
                component: 'Personal Data',
            },
            {
                name: i18n.global.t('account_settings.address_data'),
                icon: HomeIcon,
                iconActive: HomeIconFilled,
                component: 'Address Data',
            },
            {
                name: i18n.global.t('account_settings.contact_data'),
                icon: PhoneIcon,
                iconActive: PhoneIconFilled,
                component: 'Contact Data',
            },
            {
                name: i18n.global.t('account_settings.security'),
                icon: LockClosedIcon,
                iconActive: LockClosedIconFilled,
                component: 'Security',
            },
            {
                name: i18n.global.t('account_settings.membership_management'),
                icon: UserGroupIcon,
                iconActive: UserGroupIconFilled,
                component: 'Membership Management',
            },
        ],
    },
    {
        name: i18n.global.t('account_settings.profile_data'),
        icon: FolderIcon,
        iconActive: FolderIconFilled,
        children: [
            {
                name: i18n.global.t('account_settings.legal_information'),
                icon: DocumentTextIcon,
                iconActive: DocumentTextIconFilled,
                component: 'Legal Information',
            },
            {
                name: i18n.global.t('account_settings.compliance_profile'),
                icon: DocumentTextIcon,
                iconActive: DocumentTextIconFilled,
                component: 'Compliance Profile',
            },
            {
                name: i18n.global.t('account_settings.verification_headline'),
                icon: BadgeCheckIcon,
                iconActive: BadgeCheckIconFilled,
                component: 'Verification',
            },
            {
                name: i18n.global.t('account_settings.bank_data'),
                icon: CashIcon,
                iconActive: CashIconFilled,
                component: 'Bank Data',
            },
            {
                name: i18n.global.t('account_settings.tax_data'),
                icon: ReceiptTaxIcon,
                iconActive: ReceiptTaxIconFilled,
                component: 'Tax Data',
            },
            {
                name: i18n.global.t('account_settings.tax_residencies'),
                icon: ReceiptTaxIcon,
                iconActive: ReceiptTaxIconFilled,
                component: 'Tax Residencies',
            },
            {
                name: i18n.global.t('account_settings.crypto_wallet'),
                icon: CreditCardIcon,
                iconActive: CreditCardIcon,
                component: 'Crypto Wallet',
            },
            {
                name: i18n.global.t('account_settings.nyala_vault'),
                icon: LockClosedIcon,
                iconActive: LockClosedIconFilled,
                component: 'Nyala Vault',
            },
            {
                name: i18n.global.t('account_settings.signatures'),
                icon: PencilAltIcon,
                iconActive: PencilAltIconFilled,
                component: 'Signatures',
            },
            {
                name: i18n.global.t('account_settings.referral_program'),
                icon: GiftIcon,
                iconActive: GiftIconFilled,
                component: 'Referral',
            },
        ],
    },
]

export const accountLinksPersonal: NavigationItemParent[] = [
    {
        name: i18n.global.t('account_links.heading'),
        children: [
            {
                name: i18n.global.t('account_links.orders'),
                icon: CollectionIcon,
                href: getOrdersLink(),
            },
            {
                name: i18n.global.t('account_links.wallet'),
                icon: CreditCardIcon,
                href: getWalletLink(),
            },
            {
                name: i18n.global.t('account_links.secondary_market'),
                icon: TicketIcon,
                href: getSecondaryMarketLink(),
                target: '_blank',
            },
        ],
    },
]

export type PersonalAccountPage =
    | 'Dashboard'
    | 'Personal Data'
    | 'Address Data'
    | 'Contact Data'
    | 'Legal Information'
    | 'Security'
    | 'Verification'
    | 'Bank Data'
    | 'Tax Data'
    | 'Crypto Wallet'
    | 'Signatures'
    | 'Referral'
    | 'Compliance Profile'
    | 'Tax Residencies'
    | 'Membership Management'

export interface AccountNavigationStore {
    page: PersonalAccountPage | RemovableRef<PersonalAccountPage>
    tab: string | null
}

export async function hasAccountPersonalPageMissingFormFieldsForInvestment(page: PersonalAccountPage) {
    const personalAccountState = usePersonalAccountDataState()
    const person = personalAccountState.naturalProfile

    const form = useForm({
        validationSchema: getPersonalAccountValidationSchema(true)[page],
        initialValues: { account: person },
        validateOnMount: true,
    })
    const validation = await form.validate()
    return !validation.valid
}

export const useAccountNavigationState = defineStore('account-navigation', {
    state: (): AccountNavigationStore => {
        // @ts-ignore
        if (import.meta.env.PROD) {
            return {
                page: 'Dashboard',
                tab: null,
            }
        }

        return {
            page: useLocalStorage<PersonalAccountPage>('page', 'Dashboard'),
            tab: null,
        }
    },
})

export const navigateTo = async function (page: PersonalAccountPage, tab: string = null) {
    const store = useAccountNavigationState()
    const confirmed = await handleDirtyState(usePersonalAccountDataState())
    if (confirmed) {
        store.page = page
        store.tab = tab
    }
}

export async function navigateToNextPageWithMissingFieldsForInvestmentPersonal() {
    const store = usePersonalAccountDataState()
    const formFieldPages = Object.keys(getPersonalAccountValidationSchema(true)) as PersonalAccountPage[]
    for (const accountPage of formFieldPages) {
        if (await hasAccountPersonalPageMissingFormFieldsForInvestment(accountPage)) {
            await navigateTo(accountPage)
            return
        }
    }
    if (!store.isVerified) {
        await navigateTo('Verification')
    }
}

export function getHiddenNavigationSections(): Array<string> {
    return window.condaHiddenNavigationSections
}
